import React, { useState, useEffect, useCallback, useRef } from 'react';
import { t } from '../../i18n';
import { formatMontant } from '../../utils/format';
import { Plus, Search, Filter, Package, AlertTriangle, Edit, Eye, Image, ImagePlus, Camera, Star, Boxes, Upload } from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import { Button, Input, Select } from '../../components/common/Input';
import FormProduit from './FormProduit';
import DetailProduit from './DetailProduit';
import GestionCartons from './GestionCartons';
import ImportProduits from './ImportProduits';
import { useAuth } from '../../context/AuthContext';
import CameraCapture from '../../components/common/CameraCapture';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────────────────
// Galerie 3 photos : 1 grande + 2 petites + bouton ajout
// ─────────────────────────────────────────────────────────────────────────────
// Layout en grille 3×3 :
//   - La grande photo occupe les 2 colonnes de gauche et les 3 lignes (= 2/3 de
//     la largeur, hauteur pleine)
//   - 3 cellules à droite : 2 photos secondaires + 1 bouton "ajouter"
// Si une cellule n'a pas de photo, on affiche un placeholder discret.
// L'upload utilise l'endpoint existant POST /produits/:id/photos.
function GalerieProduit({ produit, peutEditer, onPhotosChanged, onOuvrirDetail }) {
  const inputRef = useRef(null);
  const [enUpload, setEnUpload] = useState(false);
  const [cameraOuverte, setCameraOuverte] = useState(false);

  const baseUrl = (process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000');

  // Trier : photo principale d'abord, puis les autres
  const photos = (produit.photos || [])
    .slice()
    .sort((a, b) => (b.principale ? 1 : 0) - (a.principale ? 1 : 0));

  // Si l'API renvoie photo_principale mais pas photos[], on retombe dessus
  const grande   = photos[0] || (produit.photo_principale ? { url: produit.photo_principale, principale: true } : null);
  const petite1  = photos[1] || null;
  const petite2  = photos[2] || null;

  const uploadPhotos = async (files) => {
    if (!files || files.length === 0) return;
    const valides = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (valides.length === 0) {
      toast.error('Seules les images sont acceptées');
      return;
    }
    setEnUpload(true);
    const fd = new FormData();
    valides.forEach(f => fd.append('photos', f));
    try {
      await api.post(`/produits/${produit.id}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${valides.length} photo${valides.length > 1 ? 's' : ''} ajoutée${valides.length > 1 ? 's' : ''}`);
      onPhotosChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur upload');
    } finally {
      setEnUpload(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // Définir une photo comme principale
  const definirPrincipale = async (photoId, e) => {
    e?.stopPropagation();
    try {
      await api.put(`/produits/photos/${photoId}/principale`);
      toast.success('Photo principale mise à jour');
      onPhotosChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
  };

  // ── Cellule réutilisable (photo OU placeholder) ──────────────────────────
  // Si la photo n'est pas principale et que l'utilisateur a les droits, on
  // affiche un bouton étoile au survol pour la promouvoir en principale.
  const cellPhoto = (photo, position) => {
    if (photo) {
      return (
        <div
          className="relative w-full h-full bg-gray-800 overflow-hidden group/cell"
        >
          <button
            type="button"
            onClick={() => onOuvrirDetail?.(produit)}
            className="w-full h-full focus:outline-none"
            title="Voir le détail"
          >
            <img
              src={`${baseUrl}${photo.url}`}
              alt={produit.nom}
              className="w-full h-full object-cover group-hover/cell:scale-110 transition-transform duration-300"
            />
          </button>
          {/* Badge étoile si principale (toujours visible) */}
          {photo.principale && (
            <div className="absolute top-1 left-1 bg-yellow-500 text-black rounded-full p-0.5 pointer-events-none" title="Photo principale">
              <Star size={position === 'large' ? 12 : 9} fill="currentColor" />
            </div>
          )}
          {/* Bouton "définir comme principale" sur les non-principales */}
          {peutEditer && !photo.principale && photo.id && (
            <button
              type="button"
              onClick={(e) => definirPrincipale(photo.id, e)}
              className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover/cell:opacity-100 transition-opacity"
              title="Définir comme photo principale"
            >
              <span className="flex items-center gap-1 bg-yellow-500 text-black text-[10px] font-bold px-2 py-1 rounded">
                <Star size={10} fill="currentColor" /> Principale
              </span>
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="w-full h-full bg-gray-800/50 border border-gray-800 flex items-center justify-center">
        <Image size={position === 'large' ? 36 : 18} className="text-gray-700" />
      </div>
    );
  };

  return (
    <>
      <div className="aspect-[3/2] grid grid-cols-3 grid-rows-3 gap-0.5 bg-gray-900 overflow-hidden relative">
        {/* Grande photo (col 1-2, row 1-3) */}
        <div className="col-span-2 row-span-3 relative">
          {cellPhoto(grande, 'large')}
          {/* Badge catégorie : superposé sur la grande photo */}
          <div className="absolute top-2 right-2 pointer-events-none">
            <Badge variant="blue">
              {produit.categorie}
            </Badge>
          </div>
        </div>

        {/* Petite photo 1 (col 3, row 1) */}
        <div className="col-start-3 row-start-1">{cellPhoto(petite1, 'small')}</div>

        {/* Petite photo 2 (col 3, row 2) */}
        <div className="col-start-3 row-start-2">{cellPhoto(petite2, 'small')}</div>

        {/* Boutons ajout (col 3, row 3) — visibles uniquement pour staff */}
        <div className="col-start-3 row-start-3 grid grid-cols-2 gap-0.5">
          {peutEditer ? (
            <>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={enUpload}
                className="w-full h-full bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/30 flex flex-col items-center justify-center gap-0.5 text-yellow-400 transition-colors disabled:opacity-50"
                title="Ajouter depuis un fichier"
              >
                {enUpload ? (
                  <div className="w-3 h-3 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <ImagePlus size={14} />
                    <span className="text-[8px] font-medium leading-none">Fichier</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setCameraOuverte(true)}
                disabled={enUpload}
                className="w-full h-full bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/30 flex flex-col items-center justify-center gap-0.5 text-yellow-400 transition-colors disabled:opacity-50"
                title="Prendre une photo avec la caméra"
              >
                <Camera size={14} />
                <span className="text-[8px] font-medium leading-none">Caméra</span>
              </button>
            </>
          ) : (
            <div className="col-span-2 w-full h-full bg-gray-800/50 flex items-center justify-center">
              <Image size={18} className="text-gray-700" />
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={e => uploadPhotos(e.target.files)}
          />
        </div>
      </div>

      {/* Modal caméra */}
      {cameraOuverte && (
        <CameraCapture
          titre={`Photo — ${produit.nom}`}
          onCapture={(files) => uploadPhotos(files)}
          onClose={() => setCameraOuverte(false)}
        />
      )}
    </>
  );
}

export default function Produits() {
  const { canEditCatalog } = useAuth();
  const [produits, setProduits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [filtreDisponible, setFiltreDisponible] = useState('');
  const [modalCreer, setModalCreer] = useState(false);
  const [modalImport, setModalImport] = useState(false);
  const [showCartons, setShowCartons] = useState(false);
  const [produitDetail, setProduitDetail] = useState(null);
  const [produitModif, setProduitModif] = useState(null);

  const chargerProduits = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filtreCategorie) params.categorie = filtreCategorie;
      if (filtreDisponible) params.disponible = filtreDisponible;
      const res = await api.get('/produits', { params });
      setProduits(res.data.produits || []);
    } catch {
      toast.error(t('commun.erreurServeur'));
    } finally {
      setLoading(false);
    }
  }, [search, filtreCategorie, filtreDisponible]);

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(chargerProduits, 300);
    return () => clearTimeout(timer);
  }, [chargerProduits]);

  const niveauStock = (variantes) => {
    if (!variantes || variantes.length === 0) return 'gray';
    const total = variantes.reduce((s, v) => s + (v.stock_disponible || 0), 0);
    const enAlerte = variantes.some(v => v.stock_disponible > 0 && v.stock_disponible <= v.seuil_alerte);
    const enRupture = variantes.some(v => v.stock_disponible === 0);
    if (enRupture) return 'red';
    if (enAlerte) return 'orange';
    return 'green';
  };

  const badgeStock = (variantes) => {
    const niveau = niveauStock(variantes);
    const total = variantes?.reduce((s, v) => s + (v.stock_disponible || 0), 0) || 0;
    const labels = { green: `${total} en stock`, orange: `⚠️ ${total} — stock faible`, red: '❌ Rupture', gray: 'Aucune variante' };
    return <Badge variant={niveau}>{labels[niveau]}</Badge>;
  };

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('produits.titre')}</h1>
          <p className="text-gray-400 text-sm mt-0.5">{produits.length} produit(s)</p>
        </div>
        {canEditCatalog && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowCartons(true)}
              className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-500 text-black px-4 py-2 rounded-lg text-sm font-medium">
              <Boxes size={16} /> Conditionnements
            </button>
            <button onClick={() => setModalImport(true)}
              className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium border border-gray-600">
              <Upload size={16} /> Importer (Excel)
            </button>
            <Button onClick={() => setModalCreer(true)}>
              <Plus size={16} />
              <span>{t('produits.ajouterProduit')}</span>
            </Button>
          </div>
        )}
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`${t('commun.rechercher')} — nom, modèle, couleur, code...`}
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-9 pr-4 py-2.5 focus:outline-none focus:border-yellow-500 text-sm"
          />
        </div>
        <select
          value={filtreCategorie}
          onChange={e => setFiltreCategorie(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
        >
          <option value="">{t('commun.tous')} — Catégories</option>
          {categories.map(c => (
            <option key={c.id} value={c.nom}>{c.nom.charAt(0).toUpperCase() + c.nom.slice(1)}s</option>
          ))}
        </select>
        <select
          value={filtreDisponible}
          onChange={e => setFiltreDisponible(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
        >
          <option value="">Tout le stock</option>
          <option value="true">En stock uniquement</option>
        </select>
      </div>

      {/* Grille produits */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
        </div>
      ) : produits.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 space-y-3">
          <Package size={40} className="text-gray-600" />
          <p className="text-gray-500">{t('commun.aucunResultat')}</p>
          {canEditCatalog && (
            <Button onClick={() => setModalCreer(true)}>
              <Plus size={16} /><span>Ajouter un premier produit</span>
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {produits.map(produit => (
            <div key={produit.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-yellow-600/50 transition-all group">
              {/* Galerie 3 photos + bouton ajout
                  On injecte categories via une prop temporaire `__categories`
                  pour que GalerieProduit puisse afficher le badge sans qu'on
                  ait à le passer en prop séparée (rétro-compat minimale). */}
              <GalerieProduit
                produit={{ ...produit, __categories: categories }}
                peutEditer={canEditCatalog}
                onPhotosChanged={chargerProduits}
                onOuvrirDetail={setProduitDetail}
              />

              {/* Infos */}
              <div className="p-4 space-y-3">
                <div>
                  <h3 className="text-white font-semibold text-sm leading-tight">{produit.nom}</h3>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {produit.variantes?.length || 0} variante(s)
                  </p>
                </div>

                {/* Stock */}
                {badgeStock(produit.variantes)}

                {/* Prix range */}
                {produit.variantes && produit.variantes.length > 0 && (
                  <div className="text-xs text-gray-400">
                    <span className="text-yellow-400 font-medium">
                      {Math.min(...produit.variantes.map(v => v.prix_vente_magasin || 0)) === Math.max(...produit.variantes.map(v => v.prix_vente_magasin || 0))
                        ? formatMontant(produit.variantes[0]?.prix_vente_magasin)
                        : `${formatMontant(Math.min(...produit.variantes.map(v => v.prix_vente_magasin || 0)))} — ${formatMontant(Math.max(...produit.variantes.map(v => v.prix_vente_magasin || 0)))}`
                      }
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex space-x-2 pt-1">
                  <button
                    onClick={() => setProduitDetail(produit)}
                    className="flex-1 flex items-center justify-center space-x-1 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg text-xs transition-colors"
                  >
                    <Eye size={13} /><span>Détail</span>
                  </button>
                  <button
                    onClick={() => setProduitModif(produit)}
                    className="flex-1 flex items-center justify-center space-x-1 py-2 bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-400 rounded-lg text-xs transition-colors"
                  >
                    <Edit size={13} /><span>Modifier</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Créer */}
      <Modal open={modalCreer} onClose={() => setModalCreer(false)} title={t('produits.ajouterProduit')} size="lg">
        <FormProduit
          categories={categories}
          onSuccess={() => { setModalCreer(false); chargerProduits(); toast.success(t('commun.succes')); }}
          onCancel={() => setModalCreer(false)}
        />
      </Modal>

      {/* Modal Import Excel */}
      <Modal open={modalImport} onClose={() => setModalImport(false)} title="Importer des produits (Excel)" size="lg">
        <ImportProduits
          categories={categories}
          onSuccess={() => { setModalImport(false); chargerProduits(); }}
          onCancel={() => setModalImport(false)}
        />
      </Modal>

      {/* Modal Détail */}
      <Modal open={!!produitDetail} onClose={() => setProduitDetail(null)} title={produitDetail?.nom || ''} size="lg">
        {produitDetail && (
          <DetailProduit
            produit={produitDetail}
            onClose={() => setProduitDetail(null)}
            onRefresh={chargerProduits}
          />
        )}
      </Modal>

      {/* Modal Modifier */}
      <Modal open={!!produitModif} onClose={() => setProduitModif(null)} title={`Modifier — ${produitModif?.nom || ''}`} size="lg">
        {produitModif && (
          <FormProduit
            produit={produitModif}
            categories={categories}
            onSuccess={() => { setProduitModif(null); chargerProduits(); toast.success(t('commun.succes')); }}
            onCancel={() => setProduitModif(null)}
          />
        )}
      </Modal>

      {showCartons && <GestionCartons onClose={() => setShowCartons(false)} />}
    </div>
  );
}
